/**
 * The address bar, taught the product's places.
 *
 * Hand-rolled on purpose: the whole job is one hash with two shapes —
 * `#/place` and `#/pages/<id>` — and a routing library would bring a route
 * tree, a render model and a dependency for a mapping this file states in
 * twenty lines. What it buys is real: a reload lands where you were, a link
 * to a place can be sent, and the browser's own Back walks the places you
 * walked.
 *
 * The hash rather than the path, because the writer is served as one static
 * bundle and a path router would need the server to answer every address
 * with the same page. The hash asks nothing of the server.
 */

/** Every place the shell can stand. The sidebar shows few; all are real. */
export type Place =
  | 'home'
  | 'pulse'
  | 'analytics'
  | 'pages'
  | 'import'
  | 'library'
  | 'variables'
  | 'redirects'
  | 'words'
  | 'search'
  | 'developers'
  | 'help'
  | 'settings'

/**
 * What each place is CALLED, once.
 *
 * The breadcrumb had this list and so did nothing else, so anything else
 * needing a place's name — the Help panel, writing a subject line for a
 * message about the screen somebody is on — had to keep a second copy. Two
 * lists of the same names drift the first time one is renamed.
 */
export const PLACE_NAMES: Record<Place, string> = {
  home: 'Home',
  pulse: 'Pulse',
  analytics: 'Analytics',
  pages: 'Pages',
  import: 'Import',
  library: 'Library',
  variables: 'Variables',
  redirects: 'Redirects',
  words: 'Words & style',
  search: 'Search',
  developers: 'Developers',
  help: 'Help & docs',
  settings: 'Settings',
}

/**
 * The same places as a list, DERIVED rather than typed a second time.
 *
 * It was typed a second time, twenty lines above the map, and the two happened
 * to agree. Nothing made them: `PLACE_NAMES` is a `Record<Place, …>`, so the
 * compiler refuses an incomplete map, while `readonly Place[]` is satisfied by
 * any subset. A place added to the union and to the map and forgotten here
 * would compile, and then `isPlace` would answer no for it — so every deep
 * link to that screen would quietly fall back to whatever was remembered, on a
 * screen that otherwise works.
 */
export const PLACES: readonly Place[] = Object.keys(PLACE_NAMES) as Place[]

export function isPlace(raw: string): raw is Place {
  return (PLACES as readonly string[]).includes(raw)
}

/**
 * What a hash says, or null for a hash that says nothing — an empty one, or
 * one from a build that named its places differently. Null means "use what
 * is remembered", never "show an error": an address is a convenience here,
 * not a claim the shell has to refuse.
 */
export function parseHash(raw: string): { place: Place; pageId: string | null } | null {
  const path = raw.replace(/^#\/?/, '').replace(/\/$/, '')
  if (!path) return null
  const slash = path.indexOf('/')
  const head = slash === -1 ? path : path.slice(0, slash)
  const rest = slash === -1 ? '' : path.slice(slash + 1)
  if (!isPlace(head)) return null
  // Only Pages carries a second half: the page's own id, slashes and all.
  if (head === 'pages' && rest) return { place: 'pages', pageId: decode(rest) }
  return { place: head, pageId: null }
}

/**
 * A page id out of an address, where a malformed escape is TEXT rather than an
 * outage.
 *
 * `decodeURIComponent` throws a URIError on a percent sign that does not begin
 * a valid escape — `#/pages/100%-done`, which is a real page id after the
 * importer stopped rewriting names, or anything somebody typed or truncated in
 * the address bar. This is called from the shell's own first render, so the
 * throw came out of `createRoot(...).render()` and the writer painted nothing
 * at all: a white page, from an address, with no way back except editing it.
 *
 * The same fault the site server had one layer down, where a malformed path
 * ended the process serving every customer. The rule this file already states
 * is the answer: an address is a convenience, never a claim the shell has to
 * refuse. So an id that will not decode is taken as written — which is what it
 * almost certainly is — and a page nobody has is simply a page that does not
 * open.
 */
function decode(raw: string): string {
  try {
    return decodeURIComponent(raw)
  } catch {
    return raw
  }
}

/**
 * The hash for a place, carrying the open page when the place is Pages and
 * the open file is a page. The site files (nav.yaml, glossary.yaml,
 * style.yaml) stay off the address: they are editing surfaces, not places a
 * link should name.
 */
export function hashFor(place: Place, path: string | null = null): string {
  if (place === 'pages' && path && path.startsWith('pages/') && path.endsWith('.md')) {
    const id = path.slice('pages/'.length, -'.md'.length)
    return `#/pages/${id.split('/').map(encodeURIComponent).join('/')}`
  }
  return `#/${place}`
}

/**
 * The address a page gets from its title.
 *
 * Here rather than inside the creator, because the New page dialog now shows
 * it while somebody types and a preview that disagrees with what is actually
 * made is worse than no preview at all. One function, two readers.
 */
export function slugFor(title: string): string {
  return (
    title
      .toLowerCase()
      .replace(/['’]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '') || 'page'
  )
}
